import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';

import { userSelectors } from 'reducers';
import {
    ORDER_BY,
    roleCategories,
    RoleNames,
    sortDirection,
    UserEnrichments,
    queryStringifyOptions,
    listingActionButtonStyle,
} from 'utils/constants';
import { useLocalStorageFilters, useMatchAtLeastOneRole, useDebounce } from 'utils/hooks';
import {
    useGetRolesQuery,
    useGetSubscriptionsQuery,
    useExportSubscriptionsMutation,
    useGetShareSubcategoriesQuery,
} from 'api/main';
import { texts } from 'utils/languages/fr';

import InvestorsRow from './InvestorsRow';
import MessageDialog from './MessageDialog';
import {
    CustomTablePagination,
    EmptyTableBody,
    EnhancedTableHead,
    QuickSearchField,
    ActionButton,
    FiltersButton,
} from 'components';
import CustomFilters from 'containers/subscriptions/CustomFilters';
import {
    TableRow,
    Table,
    TableBody,
    TableContainer,
    TableFooter,
    Typography,
    Box,
} from '@mui/material';

const tableHeads = [
    {
        id: ORDER_BY.lastName,
        width: 15,
        isSortable: true,
        label: texts.NOM,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.fundName,
        width: 13,
        isSortable: true,
        label: texts.PRODUIT,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.role,
        width: 10,
        isSortable: true,
        label: texts.PROFIL_INVESTISSEUR,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.shareCategory,
        width: 10,
        isSortable: true,
        label: texts.CATEGORIE_DE_PARTS,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.investmentSum,
        width: 14,
        isSortable: true,
        label: texts.MONTANT_INVESTI,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: 'admissionFee',
        width: 5,
        isSortable: false,
        label: 'DE',
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.finishedSubscriptionAt,
        width: 15,
        isSortable: true,
        label: texts.INVESTMENT_DATE,
        availableFor: ['servicer', 'distributor'],
    },
    {
        id: ORDER_BY.distributorCompanyName,
        width: 15,
        isSortable: true,
        label: texts.DISTRIBUTEUR,
        availableFor: ['servicer'],
    },
    {
        id: ORDER_BY.distributorFullName,
        width: 15,
        isSortable: true,
        label: texts.CONSULTANT,
        availableFor: ['distributor'],
    },
    {
        id: 'actions',
        width: 3,
        isSortable: false,
        label: '',
        availableFor: ['servicer', 'distributor'],
    },
];

const filtersKey = 'investorFilters';

const InvestorsTable = () => {
    const location = useLocation();
    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
        RoleNames.PSI,
    ]);
    const canSendMassMessaegs = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);

    const {
        data: roles = [],
        isLoading: rolesLoader,
        isSuccess: rolesFetched,
    } = useGetRolesQuery();
    const { isLoading: shareSubcategoriesLoader } = useGetShareSubcategoriesQuery();
    const [exportSubscriptions, { isLoading: exportLoading }] = useExportSubscriptionsMutation();

    const internalUser = useSelector(userSelectors.getInternalUser);

    const { product, dist, companyId, indirectSubscriptions, bankingStatementStatus } = useMemo(
        () => qs.parse(location.search, queryStringifyOptions),
        [location]
    );
    const hasOneTimeFilters = useMemo(() => !!location.search, [location]);

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [messageOpen, setMessageOpen] = useState(false);

    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);

    const activeImpersonation = useMemo(() => !!internalUser?.impersonatedByUser, [internalUser]);
    const investorRoles = useMemo(
        () => roles.filter(r => r.category?.name === roleCategories.INVESTORS),
        [roles]
    );

    const defaultFilters = useMemo(
        () => ({
            orderBy: ORDER_BY.finishedSubscriptionAt,
            role: [],
            direction: sortDirection.DESC,
            page: 1,
            subscriptionStatus: ['finished'],
            bankingStatementStatus,
            keyword,
            product,
            dist,
            companyId,
            indirectSubscriptions,
            investorClassification: 'all',
            enrichWith: [
                UserEnrichments.ROLES,
                UserEnrichments.ALERTS,
                UserEnrichments.SHARE_SUBCATEGORY,
                UserEnrichments.INVESTMENT_FUND,
                UserEnrichments.INVESTOR_PROFILE,
                UserEnrichments.USER_TUNNEL_STEPS,
                UserEnrichments.INVESTMENT_CHARACTERISTICS,
                UserEnrichments.SUBSCRIPTIONS,
                UserEnrichments.COMPANY,
                UserEnrichments.DISTRIBUTOR_COMPANY,
                UserEnrichments.CREATED_BY,
            ],
        }),
        [keyword, product, dist, companyId, indirectSubscriptions, bankingStatementStatus]
    );

    const [filters, setFilters] = useLocalStorageFilters(
        filtersKey,
        defaultFilters,
        activeImpersonation || hasOneTimeFilters || !!debouncedKeyword
    );

    const {
        data: { subscriptions: investors = [], lastPage } = { subscriptions: [], lastPage: 1 },
        isFetching: listLoader,
    } = useGetSubscriptionsQuery(
        { role: investorRoles.map(role => role.id), ...filters },
        {
            skip: !roles.length,
        }
    );

    const dataIsLoading = useMemo(
        () => rolesLoader || listLoader || shareSubcategoriesLoader,
        [rolesLoader, listLoader, shareSubcategoriesLoader]
    );

    useEffect(() => {
        setFilters(prevState => ({
            ...prevState,
            keyword: debouncedKeyword,
            orderBy: !!debouncedKeyword ? prevState.orderBy : ORDER_BY.finishedSubscriptionAt,
            direction: !!debouncedKeyword ? prevState.direction : sortDirection.DESC,
            page: !!debouncedKeyword ? 1 : prevState.page,
        }));
    }, [setFilters, debouncedKeyword]);

    const handlePageChange = useCallback(
        page => () => {
            setFilters(oldValue => ({ ...oldValue, page }));
        },
        [setFilters]
    );

    const handleApplyFilters = useCallback(
        customFilters => {
            setFilters(prevState => ({ ...prevState, ...customFilters, page: 1 }));
        },
        [setFilters]
    );

    const handleSortInvestors = useCallback(
        (property, direction) => {
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [setFilters]
    );

    const filteredColumns = useMemo(
        () =>
            tableHeads.filter(h =>
                h.availableFor.includes(userIsDistributor ? 'distributor' : 'servicer')
            ),
        [userIsDistributor]
    );

    const handleToggleFilters = useCallback(() => setFiltersOpen(prevState => !prevState), []);

    const handleInvestorsListExport = useCallback(() => {
        const { enrichWith, ...importantFilters } = filters;
        exportSubscriptions(importantFilters);
    }, [exportSubscriptions, filters]);

    useEffect(() => {
        if (rolesFetched) {
            setFilters(prevState => ({
                ...prevState,
                role: prevState.role.length ? prevState.role : investorRoles.map(role => role.id),
            }));
        }
    }, [rolesFetched, investorRoles, setFilters]);

    return (
        <React.Fragment>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" align="left" gutterBottom>
                    {texts.FINISHED_SUBSCRIPTIONS}
                </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginY={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <QuickSearchField
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        handleClear={() => setKeyword('')}
                    />
                    <FiltersButton onClick={handleToggleFilters} />
                </Box>
                {!dataIsLoading && (
                    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2}>
                        <ActionButton
                            variant="outlined"
                            sx={listingActionButtonStyle}
                            disabled={exportLoading}
                            onClick={handleInvestorsListExport}
                        >
                            {texts.EXPORTER_LISTE}
                        </ActionButton>
                        {canSendMassMessaegs && (
                            <ActionButton
                                variant="outlined"
                                sx={listingActionButtonStyle}
                                onClick={() => setMessageOpen(true)}
                            >
                                <MessageDialog
                                    filters={filters}
                                    open={messageOpen}
                                    onClose={e => {
                                        e?.stopPropagation?.();
                                        setMessageOpen(false);
                                    }}
                                />
                                {texts.SEND_MESSAGE_TO_LIST}
                            </ActionButton>
                        )}
                    </Box>
                )}
            </Box>
            {!dataIsLoading && debouncedKeyword && (
                <Typography variant="body2" gutterBottom>
                    {texts.RESULTS_FOR_KEYWORD(debouncedKeyword)}
                </Typography>
            )}
            <CustomFilters
                open={filtersOpen}
                applyFilters={handleApplyFilters}
                savedFilters={filters}
                defaultFilters={defaultFilters}
                loading={listLoader}
            />
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {filteredColumns.map(c => (
                            <col key={c.id} width={`${c.width}%`} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={filteredColumns}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSortInvestors}
                    />
                    {investors.length === 0 || dataIsLoading ? (
                        <EmptyTableBody
                            colSpan={filteredColumns.length}
                            loading={dataIsLoading}
                            message={texts.NO_RESULTS_FOUND}
                        />
                    ) : (
                        <TableBody>
                            {investors.map(investor => (
                                <InvestorsRow key={investor.id} investor={investor} />
                            ))}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={filteredColumns.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};

export default InvestorsTable;
